import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
    ArrowLeft, Loader2, Play, Square, Sparkles, Send, CheckCircle2,
    Clock, AlertCircle, XCircle, MessageSquare, FileText, Users,
    Building2, BarChart3, Mail, Zap, Package
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
    prep: { label: 'Preparing', color: 'bg-amber-100 text-amber-700', icon: Clock },
    live: { label: 'Live', color: 'bg-red-100 text-red-700', icon: Play },
    processing: { label: 'Processing', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
    completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
};

const Q_STATUS_ICON = {
    pending: <Clock className="w-3.5 h-3.5 text-stone-400" />,
    asked: <AlertCircle className="w-3.5 h-3.5 text-amber-500" />,
    answered: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />,
    skipped: <XCircle className="w-3.5 h-3.5 text-stone-300" />,
};

function PrepSection({ prep }) {
    if (!prep) return null;
    return (
        <div className="bg-white border border-stone-200 rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-bold text-stone-800 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Sparkles className="w-4 h-4 text-violet-500" /> Pre-Meeting Prep
            </h3>

            {prep.attendee_briefs?.length > 0 && (
                <div>
                    <h4 className="text-xs font-semibold text-stone-600 mb-2 flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" /> Attendee Briefs
                    </h4>
                    <div className="space-y-2">
                        {prep.attendee_briefs.map((brief, i) => (
                            <div key={i} className="bg-stone-50 rounded-lg p-3 text-xs text-stone-600">
                                <p className="font-medium text-stone-800">{brief.name || `Attendee ${i + 1}`}</p>
                                {brief.disc_type && <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full">{brief.disc_type}</span>}
                                {brief.summary && <p className="mt-1">{brief.summary}</p>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {prep.product_recommendations?.length > 0 && (
                <div>
                    <h4 className="text-xs font-semibold text-stone-600 mb-2 flex items-center gap-1">
                        <Package className="w-3.5 h-3.5" /> Product Recommendations
                    </h4>
                    <div className="space-y-2">
                        {prep.product_recommendations.map((rec, i) => (
                            <div key={i} className="bg-blue-50 rounded-lg p-3 text-xs">
                                <p className="font-medium text-blue-800">{rec.product_name || rec.name}</p>
                                {rec.reasoning && <p className="text-blue-600 mt-1">{rec.reasoning}</p>}
                                {rec.suggested_talking_point && (
                                    <p className="text-blue-500 mt-1 italic">Talking point: {rec.suggested_talking_point}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {typeof prep === 'object' && prep.company_overview && (
                <div className="bg-stone-50 rounded-lg p-3 text-xs text-stone-600">
                    <p className="font-medium text-stone-800 mb-1">Overview</p>
                    <p>{prep.company_overview}</p>
                </div>
            )}
        </div>
    );
}

function OutcomeScoreCard({ postMeeting }) {
    if (postMeeting.outcome_score == null && !postMeeting.signal) return null;
    const score = postMeeting.outcome_score ?? 0;
    const signal = postMeeting.signal || 'neutral';
    const SIGNAL_CONFIG = {
        positive: { label: 'Positive signal', bg: '#E8F4EF', fg: '#1DA063' },
        neutral:  { label: 'Neutral', bg: '#F5F5F4', fg: '#57534E' },
        warning:  { label: 'At risk', bg: '#FCECD2', fg: '#8A5A0C' },
        blocker:  { label: 'Blocked', bg: '#FDECEC', fg: '#E5484D' },
    };
    const cfg = SIGNAL_CONFIG[signal] || SIGNAL_CONFIG.neutral;
    return (
        <div
            className="rounded-xl p-5 border flex items-center gap-5"
            style={{ background: `linear-gradient(135deg, ${cfg.bg}, #FFFFFF)`, borderColor: cfg.fg + '40' }}
        >
            <div>
                <div className="text-[10px] font-bold tracking-widest uppercase" style={{ color: cfg.fg }}>
                    Outcome score
                </div>
                <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold tracking-tight" style={{ color: cfg.fg }}>{score}</span>
                    <span className="text-xs text-stone-500">/ 100</span>
                </div>
            </div>
            <div
                className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
                style={{ background: cfg.fg + '22', color: cfg.fg }}
            >{cfg.label}</div>
            {postMeeting.signal_reasoning && (
                <p className="text-xs text-stone-600 flex-1 italic">{postMeeting.signal_reasoning}</p>
            )}
        </div>
    );
}

function ChaptersCard({ chapters }) {
    if (!chapters || chapters.length === 0) return null;
    const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
    const toneColor = { ok: '#1DA063', warn: '#F2A423', neutral: '#85858F' };
    return (
        <div className="bg-white border border-stone-200 rounded-xl p-5">
            <h3 className="text-sm font-bold text-stone-800 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Chapters
            </h3>
            <ul className="divide-y divide-stone-100">
                {chapters.map((c, i) => (
                    <li key={c.chapter_id || i} className="flex items-center gap-3 py-2">
                        <span className="font-mono text-[11px] text-stone-400 w-12">{fmt(c.start_sec || 0)}</span>
                        <span
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ background: toneColor[c.tone] || toneColor.neutral }}
                        />
                        <span className="text-sm text-stone-700 flex-1">{c.name}</span>
                        {c.end_sec > c.start_sec && (
                            <span className="text-[11px] text-stone-400">
                                {Math.round((c.end_sec - c.start_sec) / 60)} min
                            </span>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function AttendeeInsightsCard({ insights }) {
    if (!insights || insights.length === 0) return null;
    return (
        <div className="bg-white border border-stone-200 rounded-xl p-5">
            <h3 className="text-sm font-bold text-stone-800 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Per-Attendee Insights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {insights.map((ins, i) => (
                    <div key={ins.contact_id || i} className="p-3 bg-stone-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold text-stone-700 truncate">
                                {ins.contact_name || ins.contact_id}
                            </span>
                            {ins.talk_time_pct != null && (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">
                                    {ins.talk_time_pct}%
                                </span>
                            )}
                        </div>
                        {ins.sentiment_trend && (
                            <div className="text-[11px] text-stone-500 mb-1">
                                Sentiment: <b className="text-stone-700">{ins.sentiment_trend}</b>
                            </div>
                        )}
                        {ins.engagement_level && (
                            <div className="text-[11px] text-stone-500 mb-2">
                                Engagement: <b className="text-stone-700">{ins.engagement_level}</b>
                            </div>
                        )}
                        {ins.key_quotes?.length > 0 && (
                            <ul className="space-y-1">
                                {ins.key_quotes.slice(0, 3).map((q, j) => (
                                    <li key={j} className="text-[11px] text-stone-600 italic border-l-2 border-violet-300 pl-2">
                                        "{q.text}"
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function PostMeetingSection({ postMeeting, onSendFollowUp, sendingEmail }) {
    if (!postMeeting) return null;
    return (
        <div className="space-y-4">
            {/* Outcome score + signal */}
            <OutcomeScoreCard postMeeting={postMeeting} />

            {/* Summary */}
            {postMeeting.summary && (
                <div className="bg-white border border-stone-200 rounded-xl p-5">
                    <h3 className="text-sm font-bold text-stone-800 flex items-center gap-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <FileText className="w-4 h-4 text-blue-500" /> Meeting Summary
                    </h3>
                    <p className="text-sm text-stone-600 whitespace-pre-wrap">{postMeeting.summary}</p>
                </div>
            )}

            {/* Chapters + Attendee insights — v2.16 redesign */}
            <ChaptersCard chapters={postMeeting.chapters} />
            <AttendeeInsightsCard insights={postMeeting.attendee_insights} />

            {/* Key Insights + Buying Signals */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {postMeeting.key_insights?.length > 0 && (
                    <div className="bg-white border border-stone-200 rounded-xl p-5">
                        <h4 className="text-xs font-bold text-stone-700 mb-2 flex items-center gap-1">
                            <Zap className="w-3.5 h-3.5 text-amber-500" /> Key Insights
                        </h4>
                        <ul className="space-y-1">
                            {postMeeting.key_insights.map((insight, i) => (
                                <li key={i} className="text-xs text-stone-600 flex items-start gap-1.5">
                                    <span className="text-amber-500 mt-0.5">-</span> {insight}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {postMeeting.buying_signals?.length > 0 && (
                    <div className="bg-white border border-stone-200 rounded-xl p-5">
                        <h4 className="text-xs font-bold text-stone-700 mb-2 flex items-center gap-1">
                            <BarChart3 className="w-3.5 h-3.5 text-emerald-500" /> Buying Signals
                        </h4>
                        <ul className="space-y-1">
                            {postMeeting.buying_signals.map((signal, i) => (
                                <li key={i} className="text-xs text-stone-600 flex items-start gap-1.5">
                                    <span className="text-emerald-500 mt-0.5">+</span> {signal}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* Action Items */}
            {postMeeting.action_items?.length > 0 && (
                <div className="bg-white border border-stone-200 rounded-xl p-5">
                    <h4 className="text-xs font-bold text-stone-700 mb-2 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5 text-blue-500" /> Action Items
                    </h4>
                    <div className="space-y-2">
                        {postMeeting.action_items.map((item, i) => (
                            <div key={i} className="flex items-start gap-2 text-xs text-stone-600 bg-stone-50 rounded-lg p-2">
                                <input type="checkbox" className="mt-0.5 rounded" readOnly />
                                <div>
                                    <p className="font-medium text-stone-800">{item.title || item.description}</p>
                                    {item.assignee && <span className="text-[10px] text-stone-400">Assigned to: {item.assignee}</span>}
                                    {item.due_date && <span className="text-[10px] text-stone-400 ml-2">Due: {item.due_date}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Deal Stage Recommendation */}
            {postMeeting.deal_stage_recommendation && (
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                    <h4 className="text-xs font-bold text-violet-800 mb-1">Deal Stage Recommendation</h4>
                    <p className="text-sm font-semibold text-violet-700">
                        {typeof postMeeting.deal_stage_recommendation === 'string'
                            ? postMeeting.deal_stage_recommendation
                            : postMeeting.deal_stage_recommendation.recommended_stage}
                    </p>
                    {(postMeeting.deal_stage_reasoning || postMeeting.deal_stage_recommendation?.reasoning) && (
                        <p className="text-xs text-violet-600 mt-1">
                            {postMeeting.deal_stage_reasoning || postMeeting.deal_stage_recommendation.reasoning}
                        </p>
                    )}
                </div>
            )}

            {/* Objections */}
            {postMeeting.objections?.length > 0 && (
                <div className="bg-white border border-stone-200 rounded-xl p-5">
                    <h4 className="text-xs font-bold text-stone-700 mb-2 flex items-center gap-1">
                        <XCircle className="w-3.5 h-3.5 text-red-500" /> Objections Raised
                    </h4>
                    <div className="space-y-2">
                        {postMeeting.objections.map((obj, i) => (
                            <div key={i} className="bg-red-50 rounded-lg p-3 text-xs">
                                <p className="font-medium text-red-700">{typeof obj === 'string' ? obj : obj.objection}</p>
                                {obj.response_given && <p className="text-stone-500 mt-1">Response: {obj.response_given}</p>}
                                {obj.resolved !== undefined && (
                                    <span className={`text-[10px] mt-1 inline-block px-1.5 py-0.5 rounded-full ${
                                        obj.resolved ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
                                    }`}>{obj.resolved ? 'Resolved' : 'Unresolved'}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Next Steps */}
            {postMeeting.next_steps?.length > 0 && (
                <div className="bg-white border border-stone-200 rounded-xl p-5">
                    <h4 className="text-xs font-bold text-stone-700 mb-2">Next Steps</h4>
                    <ol className="space-y-1">
                        {postMeeting.next_steps.map((step, i) => (
                            <li key={i} className="text-xs text-stone-600 flex items-start gap-1.5">
                                <span className="text-blue-500 font-semibold">{i + 1}.</span> {step}
                            </li>
                        ))}
                    </ol>
                </div>
            )}

            {/* Attendee Sentiment */}
            {postMeeting.attendee_sentiment?.length > 0 && (
                <div className="bg-white border border-stone-200 rounded-xl p-5">
                    <h4 className="text-xs font-bold text-stone-700 mb-2 flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" /> Attendee Sentiment
                    </h4>
                    <div className="space-y-2">
                        {postMeeting.attendee_sentiment.map((att, i) => (
                            <div key={i} className="flex items-center justify-between bg-stone-50 rounded-lg p-3 text-xs">
                                <div>
                                    <p className="font-medium text-stone-800">{att.name || att.contact_name}</p>
                                    {att.notes && <p className="text-stone-500 mt-0.5">{att.notes}</p>}
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                        att.sentiment === 'positive' ? 'bg-emerald-100 text-emerald-700' :
                                        att.sentiment === 'negative' ? 'bg-red-100 text-red-700' :
                                        'bg-stone-200 text-stone-600'
                                    }`}>{att.sentiment}</span>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                        att.engagement_level === 'high' ? 'bg-emerald-100 text-emerald-700' :
                                        att.engagement_level === 'low' ? 'bg-red-100 text-red-700' :
                                        'bg-amber-100 text-amber-700'
                                    }`}>{att.engagement_level}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Follow-Up Email */}
            {postMeeting.follow_up_email_draft && (postMeeting.follow_up_email_draft.subject || postMeeting.follow_up_email_draft.body) && (
                <div className="bg-white border border-stone-200 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-bold text-stone-700 flex items-center gap-1">
                            <Mail className="w-3.5 h-3.5 text-blue-500" /> Follow-Up Email Draft
                        </h4>
                        <Button size="sm" variant="outline" onClick={onSendFollowUp} disabled={sendingEmail} className="gap-1.5 text-xs">
                            {sendingEmail ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                            Send
                        </Button>
                    </div>
                    <div className="bg-stone-50 rounded-lg p-3 text-xs">
                        <p className="font-medium text-stone-800 mb-2">Subject: {postMeeting.follow_up_email_draft.subject}</p>
                        <div className="text-stone-600 whitespace-pre-wrap">{postMeeting.follow_up_email_draft.body}</div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function MeetingSessionDetail() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [finalizing, setFinalizing] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [generatingPrep, setGeneratingPrep] = useState(false);

    useEffect(() => { loadSession(); }, [sessionId]);

    const loadSession = async () => {
        try {
            const res = await axios.get(`${API}/meeting-sessions/${sessionId}`, { headers: getAuthHeader() });
            setSession(res.data);
        } catch (err) {
            toast.error('Failed to load session');
            navigate('/meeting-sessions');
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (status) => {
        try {
            const res = await axios.patch(`${API}/meeting-sessions/${sessionId}/status`, { status }, { headers: getAuthHeader() });
            setSession(res.data);
            toast.success(`Status updated to ${status}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update status');
        }
    };

    const generatePrep = async () => {
        setGeneratingPrep(true);
        try {
            const res = await axios.post(`${API}/meeting-sessions/${sessionId}/prep`, {}, { headers: getAuthHeader() });
            setSession(prev => ({ ...prev, prep: res.data.prep }));
            toast.success('Pre-meeting prep generated');
        } catch (err) {
            toast.error('Failed to generate prep');
        } finally {
            setGeneratingPrep(false);
        }
    };

    const processPostMeeting = async () => {
        setProcessing(true);
        try {
            const res = await axios.post(`${API}/meeting-sessions/${sessionId}/process`, {}, { headers: getAuthHeader() });
            setSession(res.data);
            toast.success('Post-meeting processing complete');
        } catch (err) {
            toast.error('Failed to process meeting');
        } finally {
            setProcessing(false);
        }
    };

    const finalize = async () => {
        setFinalizing(true);
        try {
            const res = await axios.post(`${API}/meeting-sessions/${sessionId}/finalize`, {}, { headers: getAuthHeader() });
            toast.success(`Created: 1 activity, ${res.data.created?.communication_ids?.length || 0} communications, ${res.data.created?.task_ids?.length || 0} tasks`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to finalize');
        } finally {
            setFinalizing(false);
        }
    };

    const sendFollowUp = async () => {
        setSendingEmail(true);
        try {
            const res = await axios.post(`${API}/meeting-sessions/${sessionId}/send-followup`, {}, { headers: getAuthHeader() });
            toast.success(res.data.sent ? `Email sent to ${res.data.to_emails.join(', ')}` : 'Email logged (send failed)');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send');
        } finally {
            setSendingEmail(false);
        }
    };

    if (loading || !session) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    const statusConf = STATUS_CONFIG[session.status] || STATUS_CONFIG.prep;
    const StatusIcon = statusConf.icon;
    const questionProgress = session.question_progress || [];
    const answeredCount = questionProgress.filter(q => q.status === 'answered').length;

    return (
        <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/meeting-sessions')} className="p-2 hover:bg-stone-100 rounded-lg">
                        <ArrowLeft className="w-4 h-4 text-stone-600" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {session.title || 'Untitled Meeting'}
                        </h1>
                        <div className="flex items-center gap-3 mt-1">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusConf.color}`}>
                                <StatusIcon className="w-3 h-3" /> {statusConf.label}
                            </span>
                            {session.diarization_status === 'pending' && (
                                <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700"
                                    title="Processing speaker identification on the full meeting audio"
                                    data-testid="diarization-pending"
                                >
                                    <Loader2 className="w-3 h-3 animate-spin" /> Identifying speakers…
                                </span>
                            )}
                            {session.diarization_status === 'completed' && (
                                <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-700"
                                    title="Transcript is labeled with speaker identities"
                                    data-testid="diarization-ready"
                                >
                                    <Sparkles className="w-3 h-3" /> Speaker labels ready
                                </span>
                            )}
                            {session.diarization_status === 'failed' && (
                                <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-stone-200 text-stone-600"
                                    title={session.diarization_error || 'Speaker identification failed'}
                                    data-testid="diarization-failed"
                                >
                                    Speaker labels unavailable
                                </span>
                            )}
                            {session.duration_minutes && <span className="text-xs text-stone-400">{Math.round(session.duration_minutes)} min</span>}
                            {session.created_at && <span className="text-xs text-stone-400">{new Date(session.created_at).toLocaleString()}</span>}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {session.status === 'prep' && (
                        <>
                            <Button variant="outline" size="sm" onClick={generatePrep} disabled={generatingPrep} className="gap-1.5">
                                {generatingPrep ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                Generate Prep
                            </Button>
                            <Button size="sm" onClick={() => updateStatus('live')} className="gap-1.5">
                                <Play className="w-3.5 h-3.5" /> Start Meeting
                            </Button>
                        </>
                    )}
                    {session.status === 'live' && (
                        <Button size="sm" variant="destructive" onClick={() => updateStatus('processing')} className="gap-1.5">
                            <Square className="w-3.5 h-3.5" /> End Meeting
                        </Button>
                    )}
                    {session.status === 'processing' && (
                        <Button size="sm" onClick={processPostMeeting} disabled={processing} className="gap-1.5">
                            {processing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                            Process Meeting
                        </Button>
                    )}
                    {session.status === 'completed' && session.post_meeting && (
                        <Button size="sm" variant="outline" onClick={finalize} disabled={finalizing} className="gap-1.5">
                            {finalizing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                            Create CRM Records
                        </Button>
                    )}
                </div>
            </div>

            {/* Prep Data */}
            <PrepSection prep={session.prep} />

            {/* Question Progress */}
            {questionProgress.length > 0 && (
                <div className="bg-white border border-stone-200 rounded-xl p-5">
                    <h3 className="text-sm font-bold text-stone-800 flex items-center gap-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <MessageSquare className="w-4 h-4 text-blue-500" /> Question Progress
                        <span className="text-[10px] font-normal text-stone-400 ml-auto">{answeredCount}/{questionProgress.length} answered</span>
                    </h3>
                    <div className="w-full bg-stone-100 rounded-full h-1.5 mb-4">
                        <div
                            className="bg-emerald-500 h-1.5 rounded-full transition-all"
                            style={{ width: `${questionProgress.length > 0 ? (answeredCount / questionProgress.length) * 100 : 0}%` }}
                        />
                    </div>
                    <div className="space-y-2">
                        {questionProgress.map((q, i) => (
                            <div key={q.question_id || i} className="flex items-start gap-2 py-2 border-b border-stone-50 last:border-0">
                                {Q_STATUS_ICON[q.status] || Q_STATUS_ICON.pending}
                                <div className="flex-1 min-w-0">
                                    <p className={`text-xs ${q.status === 'skipped' ? 'text-stone-400 line-through' : 'text-stone-700'}`}>
                                        {q.question_text || q.question_id}
                                    </p>
                                    {q.stage_name && (
                                        <span className="text-[10px] text-stone-400">{q.stage_name}</span>
                                    )}
                                    {q.answer_summary && (
                                        <p className="text-[11px] text-emerald-600 mt-0.5 bg-emerald-50 rounded px-2 py-1">
                                            {q.answer_summary}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Transcript */}
            {session.full_transcript && (
                <div className="bg-white border border-stone-200 rounded-xl p-5">
                    <h3 className="text-sm font-bold text-stone-800 flex items-center gap-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <FileText className="w-4 h-4 text-stone-500" /> Transcript
                    </h3>
                    <div className="text-xs text-stone-600 whitespace-pre-wrap max-h-96 overflow-y-auto bg-stone-50 rounded-lg p-4 font-mono">
                        {session.full_transcript}
                    </div>
                </div>
            )}

            {/* Post-Meeting Outputs */}
            {session.post_meeting && (
                <PostMeetingSection
                    postMeeting={session.post_meeting}
                    onSendFollowUp={sendFollowUp}
                    sendingEmail={sendingEmail}
                />
            )}
        </div>
    );
}
